import { forwardRef, useState } from "react";
import { Box, Chip, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "i18n-fe";
import { userDisplayName } from "common-fe";
import ClassSearchModal from "./ClassSearchModal";
import OfflineIndicator from "./OfflineIndicator";
import SyncStatus from "./SyncStatus";
import UnsyncedRidesIndicator from "./UnsyncedRidesIndicator";
import { useScoreContext } from "../context/ScoreContext";
import { useRidesForClass } from "../hooks/useRidesForClass";
import { classNumberLabel, formatVenueTime, nextUpcomingRide } from "../util/classDisplay";
import { rideCnoNumber } from "../util/rideDisplay";

// Sticky strip below the AppBar carrying the locked show + nav crumbs
// + position/judge chips and the connectivity indicators. Pages pass
// `nav` (an array of {label, to?} crumbs) and `judge` so the chevron
// PageBreadcrumbs can disappear in favor of these rounded chips.
// #627 — `position` is per-class (derived from the class's scribeAssignments),
// so the scoring screen passes it in; screens without a class show no chip.
// #1665 — forwardRef so AppShell can measure this Box's actual rendered
// height (it can wrap to a second chip row) and publish it as a CSS var for
// sticky children below that used to hardcode a single-line-assuming offset.
// #1701 — the test-name chip and SessionExpiredChip that used to live here
// are gone/moved (see their call sites below); the judge + ride-sequence
// chips now collapse below the `md` breakpoint instead of wrapping onto a
// second row at narrow widths.
const SessionHeader = forwardRef(function SessionHeader({
  subtitle = null,
  nav = null,
  judge = null,
  position = null,
  sequence = null,
  // #636/#647 — the class currently in view (RideList/ScoringForm pass it),
  // so the next-ride chip can be shown here rather than by the lock icon.
  // null on screens with no single class in context (e.g. ClassList).
  classId = null,
  // #1557 — the classes screen has no single class in context, so it can't
  // compute a next RIDE and used to assert "No upcoming ride" forever. It opts
  // into a next-CLASS chip instead by passing showNextClass, and supplies the
  // class itself in `nextClass` (null = nothing left, rendered as an explicit
  // empty state) plus the already-resolved `nextClassTime` (null = time unknown,
  // rendered as a name-only chip rather than a made-up time).
  showNextClass = false,
  nextClass = null,
  nextClassTime = null,
  // #2196 — true while the caller's own next-class fetch (useClassesForShow,
  // ClassList) is still in flight. `nextClass === null` is otherwise
  // ambiguous between "still loading" and "confirmed no upcoming class";
  // the caller owns that distinction (it has the loading flag), this
  // component just renders what it's told.
  nextClassLoading = false,
}, ref) {
  const { selectedShow } = useScoreContext();
  const { t } = useTranslation("score");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navItems = Array.isArray(nav) ? nav : [];
  // #718 — the class-search modal moved here (out of TopBar) so it stays
  // globally reachable off the always-present session chrome, not just a
  // top-left menu.
  const [classSearchOpen, setClassSearchOpen] = useState(false);
  // #636/#647 — the next upcoming ride, off the SAME scheduled-time order +
  // derived status (score-common) RideList uses. Reuses useRidesForClass — the
  // SAME hook + cache RideList reads — so there's no second source of truth.
  const { data, loading } = useRidesForClass(classId);
  // #2196 — `data === undefined` is the sentinel useRidesForClass now returns
  // (see its own comment) for "classId hasn't resolved / this class hasn't
  // settled yet", immune to useCachedResource's own `loading` flag never
  // resetting to true when classId changes post-mount (#2162) — a real
  // settle (local or remote) is always an object, never `undefined`. Gate the
  // chip on this instead of `loading` so a scribe never sees "No upcoming
  // ride" flash before the real answer lands.
  //
  // `data !== undefined` alone is not enough, though: useCachedResource's
  // OWN initial state is `{ data: null, loading: true, error: null }` — the
  // "nothing known yet" value at mount is `null`, not `undefined` — so on a
  // classId that's already resolved when this mounts (RideList/ClassGrid,
  // both route-param classIds, never the #2162 null->real shape), the very
  // first render has data===null and would have read as "known" here,
  // rendering "No upcoming ride" for the whole in-flight window (the
  // resolved-at-mount case #2162's fix above doesn't cover). The extra
  // `!(data === null && loading)` clause excludes that in-flight window
  // while still letting a REAL settle-to-null (offline cold cache, or a
  // fetch error with nothing cached — both set loading:false alongside the
  // null) through as "known, nothing to show".
  const rideDataKnown = classId != null && data !== undefined && !(data === null && loading);
  const nextRide = nextUpcomingRide(data?.rides ?? []);
  return (
    <Box
      ref={ref}
      data-testid="session-header"
      sx={{
        position: "sticky",
        top: 64,
        zIndex: 1200,
        bgcolor: isDark ? "grey.900" : "grey.100",
        borderBottom: 1,
        borderColor: "divider",
        px: { xs: 2, sm: 3 },
        py: 1,
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ flexGrow: 1, flexWrap: "wrap", rowGap: 0.75 }}
      >
        {selectedShow && (
          <Chip
            label={selectedShow.name}
            size="small"
            color="primary"
            sx={{ fontWeight: 600 }}
          />
        )}
        {navItems.map((item, idx) => {
          const isLast = idx === navItems.length - 1;
          // Gate clickability on the handler actually being a function, not on
          // `item.to`. A crumb carrying a `to` but no `onClick` would otherwise
          // render a clickable Chip whose onClick is undefined — MUI then throws
          // "h is not a function" on click. Callers that want a clickable crumb
          // pass an onClick (they own navigation); `to` alone is not enough.
          const clickable = !isLast && typeof item.onClick === "function";
          return (
            <Stack
              key={`${item.label}-${idx}`}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Chip
                label={item.label}
                size="small"
                variant={isLast ? "filled" : "outlined"}
                color={isLast ? "default" : "primary"}
                clickable={clickable}
                onClick={clickable ? item.onClick : undefined}
                sx={
                  isLast
                    ? { fontWeight: 600 }
                    : {
                        // primary.main reads dim against grey.900 in
                        // dark mode; force the chip label to theme
                        // text color while keeping the colored border
                        // as the "this is a link" affordance.
                        color: "text.primary",
                        "& .MuiChip-label": { color: "text.primary" },
                      }
                }
              />
              {!isLast && (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  aria-hidden
                >
                  ›
                </Typography>
              )}
            </Stack>
          );
        })}
        {position && (
          <Chip
            label={`Position: ${position}`}
            size="small"
            variant="outlined"
          />
        )}
        {/* #736 — judge is a "real system user" name display too: first +
            last, falling back to email via the shared common-fe helper.
            #1701 — collapses first at narrower widths (hidden below `md`):
            it's informational, not needed to identify or score the ride —
            Position already says which judging station this scribe sits
            at, and CNO/Rider/Horse are always visible one row down. */}
        {judge && (
          <Chip
            data-testid="judge-chip"
            label={`Judge: ${userDisplayName(judge)}`}
            size="small"
            variant="outlined"
            sx={{ display: { xs: "none", md: "inline-flex" } }}
          />
        )}
        {/* #260 — ride sequence ("Ride 2 of 10") sits after the judge so
            it isn't confused with the competitor number (CNO).
            #1701 — same narrow-width collapse as the judge chip, same
            reasoning: it's a nice-to-know, not load-bearing for scoring. */}
        {sequence && (
          <Chip
            data-testid="ride-sequence"
            label={sequence}
            size="small"
            variant="outlined"
            sx={{ display: { xs: "none", md: "inline-flex" } }}
          />
        )}
        {/* #1701 — the full test name used to also render here (#896), but
            it duplicated the SAME "Test: …" chip ScoringForm's own sticky
            ride row already shows next to Rider/Horse (#915/#1664's
            rideTestName, data-testid="ride-test-name") — one #133/#134
            triage report caught both boxed in the same screenshot. Dropped
            here, not there: this breadcrumb row is the one that was
            wrapping (report #133), and the ride row's copy sits with the
            ride identity it belongs to. Both rows are equally sticky/
            always-visible, so nothing is lost by keeping only one. */}
        {subtitle && navItems.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 1 }}
          >
            {subtitle}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {/* #718 — "Find a class", moved off the top-left hamburger menu and
            in front of the next-ride pill. Same theme-token styling as
            header-next-ride (never MUI `primary`, dark grey/invisible on
            grey.900); `clickable` adds the hover affordance the plain
            display-only chips beside it don't need. */}
        <Chip
          data-testid="header-find-class"
          size="small"
          variant="outlined"
          clickable
          icon={<SearchIcon fontSize="small" />}
          label={t("classSearch.menuItem")}
          onClick={() => setClassSearchOpen(true)}
          sx={{
            bgcolor: "background.paper",
            color: "text.secondary",
            borderColor: "divider",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "text.secondary" },
          }}
        />
        {/* #1557 — the classes screen shows the next CLASS (earliest one with
            no scores in it) instead of the next ride, which it has no class
            context to compute. Same theme-token styling as the ride chip. */}
        {showNextClass ? (
          // #2196 — a distinct testid from the settled chip (never share one
          // across two render branches, see #2197). `nextClassLoading` is a
          // compound gate in the caller: useClassesForShow's own `loading`
          // OR the class-status read still being unsettled for a non-empty
          // class list. So it covers BOTH fetches, and still goes false once
          // either settles to a real "nothing left" answer — including a show
          // whose class list settles empty, which must never spin forever.
          nextClassLoading ? (
            <Chip
              data-testid="header-next-class-loading"
              size="small"
              variant="outlined"
              icon={<CircularProgress size={14} sx={{ color: "text.secondary" }} />}
              label={t("header.loading")}
              sx={{
                bgcolor: "background.paper",
                color: "text.secondary",
                borderColor: "divider",
                fontWeight: 600,
              }}
            />
          ) : (
            <Chip
              data-testid="header-next-class"
              size="small"
              variant="outlined"
              label={
                nextClass
                  ? nextClassTime
                    ? t("header.nextClassAt", {
                        time: formatVenueTime(nextClassTime),
                        name: classNumberLabel(nextClass),
                      })
                    : t("header.nextClassNoTime", { name: classNumberLabel(nextClass) })
                  : t("header.noNextClass")
              }
              sx={{
                bgcolor: "background.paper",
                color: "text.secondary",
                borderColor: "divider",
                fontWeight: 600,
              }}
            />
          )
        ) : (
          /* #647 — glanceable next ride, moved off ScribeLock into the session
             chrome. Theme tokens only (never MUI `primary`, which is dark grey
             in this brand and invisible against grey.900 in dark mode).
             #1557 — only rendered where a class is actually in context; without
             one there is no next ride to compute, and claiming "No upcoming
             ride" was asserting a negative this screen cannot know.
             #2196 — while a class IS in context but its ride fetch hasn't
             settled (`rideDataKnown` false), show a loading chip instead of
             asserting "No upcoming ride" off data that hasn't arrived yet. */
          classId != null &&
          (rideDataKnown ? (
            <Chip
              data-testid="header-next-ride"
              size="small"
              variant="outlined"
              label={
                nextRide
                  ? `${t("header.nextRideAt", { time: formatVenueTime(nextRide.scheduledTime) })} · ${t(
                      "rides.ridePrefix",
                      { number: rideCnoNumber(nextRide) },
                    )}${
                      nextRide.riderName && nextRide.riderName !== "—"
                        ? ` · ${nextRide.riderName}`
                        : ""
                    }`
                  : t("header.noNextRide")
              }
              sx={{
                bgcolor: "background.paper",
                color: "text.secondary",
                borderColor: "divider",
                fontWeight: 600,
              }}
            />
          ) : (
            <Chip
              data-testid="header-next-ride-loading"
              size="small"
              variant="outlined"
              icon={<CircularProgress size={14} sx={{ color: "text.secondary" }} />}
              label={t("header.loading")}
              sx={{
                bgcolor: "background.paper",
                color: "text.secondary",
                borderColor: "divider",
                fontWeight: 600,
              }}
            />
          ))
        )}
        <UnsyncedRidesIndicator />
        {/* #1701 — SessionExpiredChip moved to TopBar, on the top nav line
            next to the profile avatar (bug #136): it was easy to miss down
            here, and it was taking width from the row that was wrapping. */}
        <SyncStatus />
        <OfflineIndicator />
      </Stack>
      <ClassSearchModal
        open={classSearchOpen}
        onClose={() => setClassSearchOpen(false)}
        showId={selectedShow?.id}
      />
    </Box>
  );
});

export default SessionHeader;
